<template>
  <FormContainer
    withoutDivider
    id="modules-tableau-scroll"
  >
    <template #title>
      Liste des module(s) de formation
      <BasicAlert
        v-if="metaModuleEnCours?.$messageErreurDirty!"
        :alertLabel="metaModuleEnCours?.$messageErreurDirty!"
      />
    </template>
    <template #message>
      <BasicAlert
        v-if="nbMaxModulesMessage"
        alert-type="info"
        :alert-label="nbMaxModulesMessage"
        style="margin: -16px 0"
      />
    </template>
    <template
      #header-actions
      v-if="estReservation"
    >
      <BasicButton
        variant="outlined"
        :label="!nbMaxModulesMessage ? 'Ajouter un module de formation' : 'Nombre maximum de modules atteint'"
        :icon-name="!nbMaxModulesMessage ? 'add' : 'danger'"
        @click="afficherModale = true"
        :disabled="!!nbMaxModulesMessage || readonly"
      />
    </template>
    <template
      #content
      v-if="listeModules && listeModules.length"
    >
      <!-- Desktop version -->
      <div class="elements--desktop">
        <div class="cardLayoutWrapper cardLayoutWrapper--header">
          <div class="elem elem--span-2"></div>
          <div class="elem elem--span-2">
            <span>N°</span>
          </div>
          <div class="elem elem--span-1"></div>
          <div class="elem elem--span-4">
            <span>
              Type de module
              <span
                title="Formation externe, bilan de positionnement, bilan d’évaluation"
                class="input-label__icon"
              >
                <BasicIcon
                  name="help"
                  active
                  color="grey-800"
                />
              </span>
            </span>
          </div>
          <div class="elem elem--span-6">
            <span>Date de début</span>
          </div>
          <div class="elem elem--span-6">
            <span>Date de fin</span>
          </div>
          <div class="elem elem--span-6">
            <span>Modalité pédagogique</span>
          </div>
          <div class="elem elem--span-8">
            <span>Organisme de formation</span>
          </div>
        </div>

        <div
          v-for="(module, index) in listeModules"
          class="listCardDesktopWrapper"
          :key="index"
        >
          <div
            class="cardLayoutWrapper cardLayoutWrapper--selectionnable"
            :class="{ isActive: activeModuleIndex === index }"
            @click="activerModule(index)"
          >
            <BasicAlert
              v-if="meta.formation?.modules?.at(index)?.$estErreur"
              :has-bg="false"
              class="elem elem--span-2"
            />
            <div
              v-else
              class="elem elem--span-2"
            ></div>
            <div class="elem elem--span-2">
              <BasicText>{{ index + 1 }}</BasicText>
            </div>
            <div class="elem elem--span-1"></div>
            <div class="elem elem--span-4">
              <BasicText>{{ getModuleLabel(module.typeModule!) }}</BasicText>
            </div>
            <div class="elem elem--span-6">
              <BasicText>{{ dateFormatter(module.dateDebut) }}</BasicText>
            </div>
            <div class="elem elem--span-6">
              <BasicText>{{ dateFormatter(module.dateFin) }}</BasicText>
            </div>
            <div class="elem elem--span-6">
              <BasicText>
                {{ module.modalitePedagogique ? ModalitePedagogiqueLibelle[module.modalitePedagogique] : '' }}
              </BasicText>
            </div>
            <div class="elem elem--span-8">
              <BasicText>{{ module.etablissementPrestataire?.denomination }}</BasicText>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile version -->
      <div class="elements--mobile">
        <div
          v-for="(module, index) in listeModules"
          :key="index"
          :class="['card', { 'card--active': activeModuleIndex === index }]"
          @click="activerModule(index)"
        >
          <div class="card__header">
            <BasicText weight="bold">
              #{{ index + 1 }} Organisme de formation {{ getModuleLabel(module.typeModule!) }}
            </BasicText>
          </div>
          <div class="card__content">
            <BasicText>
              <BasicText weight="bold">Du</BasicText>
              {{ dateFormatter(module.dateDebut) }}
              <BasicText weight="bold">au</BasicText>
              {{ dateFormatter(module.dateFin) }}
            </BasicText>
            <BasicText wrap>
              <BasicText weight="bold">Modalité pédagogique :</BasicText>
              {{ module.modalitePedagogique ? ModalitePedagogiqueLibelle[module.modalitePedagogique] : '' }}
            </BasicText>
          </div>
        </div>
      </div>
    </template>
    <template
      #content
      v-else
    >
      <!-- Empty state -->
      <BasicAlert
        v-if="meta.formation?.modules && meta.formation?.modules.$estErreurDirty"
        :alertLabel="meta.formation?.modules.$messageErreurDirty!"
      />
      <EmptyDataTable>
        <template #content>
          Retrouvez ici la
          <strong>liste des modules de formation que vous avez ajoutés</strong>
          (jusqu'à {{ compteursMax.nbMaxModules }} maximum). Vous devez en ajouter un au minimum.
        </template>
      </EmptyDataTable>
    </template>
  </FormContainer>
</template>

<script setup lang="ts">
  import {
    DEMANDE_STORE,
    EmptyDataTable,
    FormContainer,
    useNombreMaxSalariesModules,
  } from '@/features/interface/demande/shared'
  import { dateFormatter } from '@/features/shared/tools'
  import BasicIcon from '@designSystem/components/basic/icon/BasicIcon.vue'
  import { storeToRefs } from 'pinia'
  import { inject, nextTick } from 'vue'
  import { CXPI_MODULES_ACTIONS } from '../../useModuleActions'
  import { ModalitePedagogiqueLibelle } from '@/features/domaine/types/opco/Demande/CXPI'

  const store = inject(DEMANDE_STORE)!.getStore<'MODEL_CXPI'>()
  const { meta, readonly, estReservation } = storeToRefs(store)
  const { nbMaxModulesMessage, compteursMax } = useNombreMaxSalariesModules(store)

  const { afficherModale, activeModuleIndex, listeModules, définirModuleEnCours, getModuleLabel, metaModuleEnCours } =
    inject(CXPI_MODULES_ACTIONS)!

  function activerModule(index: number) {
    const newIndex = activeModuleIndex.value === index ? null : index
    définirModuleEnCours(newIndex)

    nextTick(() => {
      const scrollName = 'module-scroll-externe'

      document.getElementById(scrollName)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }
</script>
<style lang="less" scoped>
  .input-label__icon {
    svg path {
      fill: pink;
    }
  }
</style>
